/**
 * hooks/useIsReadOnly.js
 * ──────────────────────────────────────────────────────────────
 * Feb 2026 (Item 6 — VIEW_ONLY role).
 *
 * Returns true when the currently-logged-in user has the `Viewer`
 * role (read-only across every module). Backed by the same
 * authService-managed localStorage entry the rest of the app
 * reads from.
 *
 * NOTE
 *   This is the single frontend gate referenced by the `<Button>`
 *   wrapper (components/ui/button.jsx). Any other component that
 *   needs to hide a non-button write affordance (a clickable
 *   list-item delete icon, a swipe-to-approve gesture, etc.) can
 *   import this hook and short-circuit its render. Backend
 *   enforcement is independent (viewerReadOnlyGuard middleware)
 *   so even if a button slips through, the API rejects with 403
 *   code READ_ONLY_ROLE.
 */
import { useEffect, useState } from 'react';
import { authService } from '../services/authService';

const VIEWER_ROLE = 'Viewer';

function currentUserRole() {
  try {
    const u = authService.getCurrentUser();
    return u?.role || null;
  } catch (_) {
    return null;
  }
}

export function useIsReadOnly() {
  const [role, setRole] = useState(currentUserRole);

  useEffect(() => {
    // Re-read on storage events so a logout/login in another tab
    // propagates immediately without a full page reload.
    const onStorage = (e) => {
      if (e.key === 'user' || e.key === null) {
        setRole(currentUserRole());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return role === VIEWER_ROLE;
}

/** Non-hook variant for class components / utility code. */
export function isReadOnlyRole() {
  return currentUserRole() === VIEWER_ROLE;
}
